import {
  Box,
  Button,
  Divider,
  Grid,
  Paper,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Parser, ProcessNodeDefinitions } from 'html-to-react';
import React from 'react';

import { LabeledField } from 'components/labeled-field/labeled-field';

import { GeneralSearchEntityType, GeneralSearchTypeToPath } from 'enums';
import {
  mapSearchEntityType,
  useUpdateSearchFormByResult,
} from './search-utils';
import {
  NewCentralFormType,
  SearchEntityType,
  SearchEntityTypeOptionId,
  SearchResultDto,
  SearchSelectedItems,
} from './types';

const useStyles = makeStyles(() => ({
  container: {
    padding: '1rem',
    cursor: 'pointer',
    '&:hover': {
      boxShadow: '0 0 10px 2px rgba(0, 0, 0, 0.1)',
    },
  },
  title: {
    fontSize: 17,
  },

  titleRow: {
    marginBottom: '0.5rem',
    display: 'flex',
    justifyContent: 'space-between',
    height: '100%',
    flexGrow: 1,
  },
  button: {
    textTransform: 'none',
    padding: '4px 16px 4px 16px',
  },
  divider: {
    margin: '0.5rem 0',
  },
  emphesised: {
    fontWeight: 700,
    backgroundColor: 'red',
  },
}));

type SearchResultProps = {
  item: SearchResultDto;
};

const parser = new Parser();

const isValidNode = (node: any) => {
  return true;
};

const processNodeDefinitions = new ProcessNodeDefinitions(React);
const parsingInstructions = [
  {
    shouldProcessNode: (node: Record<string, any>) => node?.name === 'em',
    processNode: (_node: Record<string, any>, children: any) => (
      <span
        style={{
          fontWeight: 600,
          background: '#cf29314a',
          border: '1px solid white',
          borderRadius: '8px',
          padding: '0 4px',
        }}
      >
        {children}
      </span>
    ),
  },
  {
    shouldProcessNode: () => true,
    processNode: processNodeDefinitions.processDefaultNode,
  },
];

const parseHtml = (html: string) =>
  parser.parseWithInstructions(html, isValidNode, parsingInstructions);

const getItemLink = (item: SearchResultDto): string => {
  //TODO: refactor
  const baseUrl = `${window.location.origin}${process.env.PUBLIC_URL}/${
    GeneralSearchTypeToPath[item.archivalEntityType]
  }`;

  if (item.archivalEntityType === GeneralSearchEntityType.EAD_NODE) {
    return `${baseUrl}/2/${item.id}/ead`;
  }

  return `${baseUrl}/${item.id}`;
};

const fields: Record<
  SearchEntityTypeOptionId,
  {
    label: string;
    field: keyof SearchResultDto;
    mapValue?: (value: unknown) => string;
    renderField?: (item: SearchResultDto) => boolean;
  }[]
> = {
  COLLECTIONS: [
    { label: 'časový rozsah', field: 'timeRange' },
    { label: 'číslo NAD', field: 'evidenceNumber' },
    { label: 'metráž', field: 'sumQuantity' },
  ],
  AID: [
    { label: 'časový rozsah', field: 'timeRange' },
    { label: 'autor', field: 'authors' },
    { label: 'číslo pomůcky', field: 'evidenceNumber' },
    { label: 'druh pomůcky', field: 'type' },
    { label: 'rok vytvoření', field: 'yearOfOrigin' },
  ],
  ARCHIVAL_UNIT_OR_EAD: [
    { label: 'fond', field: 'archivalCollectionName' },
    { label: 'úroveň', field: 'levelLabel' },
    { label: 'referenční označení', field: 'referenceId' },
    { label: 'časový rozsah', field: 'timeRange' },
    {
      label: 'typ archiválie',
      field: 'evidenceUnitsUnitKind',
      renderField: (item: SearchResultDto) => {
        return item.evidenceUnitsUnitKind.toUpperCase() !== 'SLOŽKA';
      },
    },
    {
      label: 'přístupný digitální obsah',
      field: 'availableDigitalContent',
      mapValue: (v: unknown) => (v ? 'Ano' : 'Ne'),
    },
  ],
  ORIGINATOR: [
    { label: 'třída', field: 'classLabel' },
    { label: 'podtřída', field: 'subclassLabel' },
  ],
};

const getItemPrefix = (type: GeneralSearchEntityType): string => {
  if (type === GeneralSearchEntityType.ARCHIVAL_COLLECTION) {
    return 'fond';
  }
  if (type === GeneralSearchEntityType.ARCHIVAL_AID) {
    return 'pomůcka';
  }
  if (type === GeneralSearchEntityType.ORIGINATOR) {
    return '';
  }

  return 'archivní popis/archiválie';
};

const AddToFilterButton = ({ item }: SearchResultProps) => {
  const { updateForm, currentFormValues } = useUpdateSearchFormByResult();

  const styles = useStyles();

  if (item.archivalEntityType === 'EAD_NODE') {
    return <></>;
  }

  if (item.archivalEntityType === 'ARCHIVAL_UNIT') {
    return <></>;
  }

  if (item.archivalEntityType === 'ORIGINATOR') {
    return (
      <Box display="flex" flexDirection="column" alignItems="end">
        <Button
          className={styles.button}
          disabled={(currentFormValues?.specificFilterRows ?? []).some(
            (row) =>
              row.filterOption?.id === 'archivalUnits-roles' &&
              row.customFilterValues?.[1]?.value?.id === item.label
          )}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            updateForm(item, 'RELATION');
          }}
        >
          <Typography component="span">
            Přidat do filtru (rej. heslo v roli)
          </Typography>
        </Button>

        <Button
          className={styles.button}
          disabled={(currentFormValues?.specificFilterRows ?? []).some(
            (row) =>
              row.filterOption?.id === 'originators-general' &&
              row.customFilterValues?.[1]?.value?.id === item?.label
          )}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            updateForm(item, 'ORIGINATOR');
          }}
        >
          <Typography component="span"> Přidat do filtru (původce)</Typography>
        </Button>
      </Box>
    );
  }

  const entityTypeMap: Record<string, keyof NewCentralFormType> = {
    ARCHIVAL_AID: 'selectedAids',
    ARCHIVAL_COLLECTION: 'selectedCollections',
    ORIGINATOR: 'specificFilterRows',
  };

  const isDisabled = !!(currentFormValues?.[
    entityTypeMap[item.archivalEntityType]
  ] as SearchSelectedItems)?.items?.find?.((sitem) => sitem.id === item.id);

  return (
    <Button
      disabled={isDisabled}
      className={styles.button}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        updateForm(item);
      }}
    >
      {isDisabled ? (
        <Typography component="span">Přidáno do filtru</Typography>
      ) : (
        <Typography component="span">+ Přidat do filtru</Typography>
      )}
    </Button>
  );
};

export const SearchResultItem = ({ item }: SearchResultProps) => {
  const { label, archivalEntityType, hitFields } = item;
  const styles = useStyles();

  return (
    <Grid
      container
      component={Paper}
      elevation={0}
      className={styles.container}
      onClick={() => window.open(getItemLink(item), '_blank')}
    >
      <Grid item xs={12} className={styles.titleRow}>
        <Typography className={styles.title}>
          <b>{getItemPrefix(item.archivalEntityType)}</b> {label}
        </Typography>
        <AddToFilterButton item={item} />
      </Grid>

      {fields[mapSearchEntityType?.[archivalEntityType as SearchEntityType]]
        .filter((f) => (item?.[f.field] ?? undefined) !== undefined)
        .filter((f) => (f.renderField ? f.renderField(item) : true))
        .map((f, index) => {
          return (
            <Grid item md={12} xs={12} key={`${f.label}-${index}`}>
              <LabeledField
                label={f.label}
                content={
                  f.mapValue
                    ? f.mapValue(item?.[f.field])
                    : item?.[f.field] ?? '--'
                }
              />
            </Grid>
          );
        })}
      <Box flexGrow={1} minHeight="10px" />
      <Grid item xs={12} className={styles.divider}>
        <Divider variant="fullWidth" />
      </Grid>
      {Object.keys(hitFields)
        .filter((key) => key !== 'archivalEntityType')
        .map((key, index) => (
          <Grid item xs={12} key={`hit-field-${key}-${index}`}>
            <Typography>
              <b>{key}: </b>
              {parseHtml(hitFields[key])}
            </Typography>
          </Grid>
        ))}
    </Grid>
  );
};
